import FilePicker from '@rio-cloud/rio-uikit/FilePicker';
import Tag from '@rio-cloud/rio-uikit/Tag';
import TagList from '@rio-cloud/rio-uikit/TagList';
import { isEmpty } from 'es-toolkit/compat';
import type React from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { fileTypeHelper } from './utils';

const TOTAL_FILES_SIZE_LIMIT = 15728640; // 15MB
const MAX_FILE_NAME_LENGTH = 128;

interface FilePickerProps {
    setFiles: (files: File[]) => void;
    files: File[];
}

const FilePickerComponent: React.FC<FilePickerProps> = ({ files, setFiles }) => {
    const { mimeTypes, extensions } = useMemo(() => fileTypeHelper(), []);
    const intl = useIntl();
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const handlePick = (files: File[]) => {
        if (!files) {
            setFiles([]);
            return;
        }
        const acceptedFiles = Array.from(files);
        const totalSize = acceptedFiles.reduce((acc, file) => acc + file.size, 0);
        const invalidFiles = acceptedFiles.filter(file => !mimeTypes.includes(file.type));
        const oversizedFileNameExists = acceptedFiles.some(file => file.name.length > MAX_FILE_NAME_LENGTH);

        if (oversizedFileNameExists) {
            setErrorMessage(
                intl.formatMessage({ id: 'contactForm.fileNameExceedsLimit' }, { limit: MAX_FILE_NAME_LENGTH })
            );
            return;
        }
        if (totalSize > TOTAL_FILES_SIZE_LIMIT) {
            setErrorMessage(
                intl.formatMessage(
                    { id: 'contactForm.fileSizeExceedsLimit' },
                    { limit: TOTAL_FILES_SIZE_LIMIT / 1024 / 1024 }
                )
            );
            return;
        }
        if (invalidFiles.length) {
            setErrorMessage(
                intl.formatMessage(
                    { id: 'contactForm.error.invalidFileTypes' },
                    { allowedFileTypes: extensions.join(', ') }
                )
            );
            return;
        }

        setErrorMessage(undefined);
        setFiles(acceptedFiles);
    };

    const removeByPath = (fileName: string) => {
        const newFiles = files.filter(file => file.name !== fileName);
        setFiles(newFiles);
    };

    const alertMessage = (
        <div className='alert alert-dismissible alert-danger margin-bottom-20'>
            <button
                type='button'
                className='btn btn-icon-only close'
                data-dismiss='alert'
                onClick={() => setErrorMessage(undefined)}
            >
                <span className='uikit-icon icon-remove' />
            </button>
            <div className='display-flex gap-10'>
                <span className='text-color-danger text-size-h4 uikit-icon icon-error-sign' />
                <div>
                    <div className='margin-y-5'>{errorMessage}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div>
            {errorMessage && alertMessage}
            <div className='col-xs-12 col-ls-2 padding-left-0'>
                <FilePicker
                    displayMode={FilePicker.DISPLAY_MODE_BUTTON}
                    label={intl.formatMessage({ id: 'contactForm.attachments.button' })}
                    onPick={handlePick}
                    multiple={true}
                >
                    {({ isDragActive }) => <DropArea isDragActive={isDragActive} />}
                </FilePicker>
            </div>
            <div className='col-xs-12 col-ls-10 max-height-60 overflow-y-auto'>
                {!isEmpty(files) && (
                    <TagList>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            {files.map(file => (
                                <FilePreview key={file.name} file={file} onFileRemove={removeByPath} />
                            ))}
                        </div>
                    </TagList>
                )}
            </div>
        </div>
    );
};

// biome-ignore lint/suspicious/noExplicitAny: false positive
const DropArea = ({ isDragActive }: any) => (
    <div className='rounded border border-color-gray border-style-dashed bg-white'>
        <div
            className={'bg-lightest text-size-14 text-center text-color-darkest opacity-50 cursor-pointer padding-y-10'}
        >
            <span className='uikit-icon icon-files text-size-h1 opacity-50 margin-top-20' />
            <div className='margin-top-10'>
                {isDragActive ? (
                    <FormattedMessage id={'contactForm.dropzone.dropAttachments'} />
                ) : (
                    <FormattedMessage id={'contactForm.dropzone.dragAttachments'} />
                )}
            </div>
        </div>
    </div>
);

interface FilePreviewProps {
    file: File;
    onFileRemove: (fileName: string) => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onFileRemove }) => (
    <button
        type='button'
        onClick={() => onFileRemove(file.name)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        aria-label={`Remove file ${file.name}`}
    >
        <Tag deletable size='small'>
            {file.name}
        </Tag>
    </button>
);

export default FilePickerComponent;
